import { Controller } from 'react-hook-form';

import { useAppTranslations } from '@treneo/shared/i18n';
import { Autocomplete, TextField } from '@treneo/shared/ui';

import { translateErrorMessage } from '../constants/form-constants';
import { useAppFormContext } from '../hooks';

type Option = {
  id: string | number;
  label: string;
  description?: string;
};

type Props = {
  name: string;
  label: string;
  options: Option[];
  required?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  slotProps?: Record<string, unknown>;
};

export const AutocompleteField = ({
  name,
  label,
  options,
  required = false,
  fullWidth = true,
  disabled = false,
  placeholder,
  helperText,
  slotProps
}: Readonly<Props>) => {
  const { control } = useAppFormContext();
  const { t } = useAppTranslations();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? t('validation.required') : false }}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const selectedOption = options.find(option => option.id === value) ?? null;

        return (
          <Autocomplete
            options={options}
            getOptionLabel={option => option.label}
            value={selectedOption}
            onChange={(_, newValue) => {
              onChange(newValue ? newValue.id : '');
            }}
            disabled={disabled}
            renderInput={params => {
              const rawMessage = error ? translateErrorMessage(error) : '';
              let helperTextResolved = helperText;
              if (error && rawMessage) {
                const translated = t(rawMessage);
                const hasTranslation = translated !== rawMessage;
                helperTextResolved = hasTranslation ? translated : rawMessage;
              }
              return (
                <TextField
                  {...params}
                  label={label}
                  required={required}
                  fullWidth={fullWidth}
                  error={!!error}
                  helperText={helperTextResolved}
                  placeholder={placeholder}
                  slotProps={{ ...slotProps, inputLabel: { shrink: true } }}
                />
              );
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        );
      }}
    />
  );
};
